import React, { useContext, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useFetch from '../hooks/useFetch';
import RelatedProducts from '../components/RelatedProducts';
import { FaChevronDown, FaChevronUp, FaBox } from "react-icons/fa";
import { CartContext } from '../context/CartContext';
import ThemedSuspense from "../components/ThemedSuspense";
import { Helmet } from "react-helmet-async";

const ProductDetails = () => {
    const { addToCart } = useContext(CartContext);
    const { name } = useParams();
    const [productId, setProductId] = useState();

    const { data: allProducts } = useFetch(`/products`);
    const rate = useFetch('/rates');

    const slugify = (str) =>
        str.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();

    useEffect(() => {
        if (allProducts && allProducts.length > 0) {
            const found = allProducts.find(p => slugify(p.name) === name);
            if (found) setProductId(found.id);
        }
    }, [allProducts, name]);

    const { data } = useFetch(productId ? `/products/${productId}` : null);

    const [showFullDescription, setShowFullDescription] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    // refs для скролла миниатюр
    const thumbScrollRef = useRef(null);

    const scrollThumbs = (direction) => {
        if (!thumbScrollRef.current) return;
        const scrollAmount = 120; // примерно высота одного thumb + gap
        if (direction === 'up') {
            thumbScrollRef.current.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
        } else {
            thumbScrollRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        }
    };

    // Инициализация выбранного изображения
    useEffect(() => {
        if (!data) return;

        if (data.thumbnail) {
            // Default har doim thumbnail
            setSelectedImage(data.thumbnail);
        } else if (data.display_images && data.display_images.length > 0) {
            // fallback agar thumbnail bo'lmasa
            setSelectedImage(data.display_images[0].image);
        }
    }, [data]);

    const toggleDescription = () => setShowFullDescription(prev => !prev);

    if (!data || !data.category) return <ThemedSuspense />;

    const categoryTitle = data?.category?.category_name;

    const kurs = rate.data && rate.data.length > 0 ? rate.data[0].rate : null;

    const displayImagesFromApi = data?.display_images?.map((imgObj) => imgObj.image) || [];

    // Мок-данные для остальных картинок (если в базе только одна)
    const allImages = data?.thumbnail
        ? [data.thumbnail, ...displayImagesFromApi]  // thumbnail boshida
        : displayImagesFromApi.length
            ? displayImagesFromApi
            : data?.images?.length
                ? data.images
                : [];

    const getImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `https://api.max-photo.uz${url}`;
    };

    // Если в базе только одна картинка — делаем мок из 5 одинаковых (чтобы секция выглядела заполненной)
    const displayImages = allImages.length === 1
        ? Array(5).fill(allImages[0])
        : allImages;

    const formatDescription = (text) => {
        const lines = text.split('\n');
        const elements = [];
        let listItems = [];

        lines.forEach((line, i) => {
            const trimmed = line.trim();

            // Заголовки ###
            if (trimmed.startsWith('###')) {
                if (listItems.length > 0) {
                    elements.push(<ul key={`ul-${i}`} className="list-disc ml-6 space-y-2 my-4">{listItems}</ul>);
                    listItems = [];
                }
                elements.push(
                    <h3 key={i} className="text-xl font-bold text-accent mt-6 mb-3">
                        {trimmed.replace(/###/g, '').replace(/\*\*/g, '').trim()}
                    </h3>
                );
            }
            // Списки -
            else if (trimmed.startsWith('-')) {
                const text = trimmed.replace(/^-\s*/, '');
                const formatted = text.split('**').map((part, idx) =>
                    idx % 2 === 1 ? <strong key={idx} className="font-semibold text-white">{part}</strong> : part
                );
                listItems.push(<li key={i} className="text-sm leading-relaxed">{formatted}</li>);
            }
            // Обычный текст
            else if (trimmed) {
                if (listItems.length > 0) {
                    elements.push(<ul key={`ul-${i}`} className="list-disc ml-6 space-y-2 my-4">{listItems}</ul>);
                    listItems = [];
                }
                const formatted = trimmed.split('**').map((part, idx) =>
                    idx % 2 === 1 ? <strong key={idx} className="font-semibold text-white">{part}</strong> : part
                );
                elements.push(<p key={i} className="mb-3 text-sm leading-relaxed">{formatted}</p>);
            }
        });

        if (listItems.length > 0) {
            elements.push(<ul key="ul-final" className="list-disc ml-6 space-y-2 my-4">{listItems}</ul>);
        }

        return elements;
    };

    const collections = data?.collections || [];

    return (
        <>
            <Helmet>
                <title>{data?.name} | MaxPhoto</title>
                <meta name="description" content={data?.description?.slice(0, 150)} />
                <meta property="og:title" content={name} />
                <meta property="og:description" content={data?.description?.slice(0, 150)} />
                <meta property="og:image" content={getImageUrl(data.thumbnail)} />
            </Helmet>

            <div className='mb-16 pt-44 lg:pt-[30px] xl:pt-0'>
                <div className='container mx-auto'>
                    <div className='flex flex-col lg:flex-row gap-[30px] mb-[50px] relative'>

                        <div className='flex-1 lg:max-w-[45%] flex gap-6 items-start'>

                            <div className='relative flex flex-col items-center'>

                                <button
                                    onClick={() => scrollThumbs('up')}
                                    className='mb-2 p-2 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition'
                                >
                                    <FaChevronUp className='text-white text-xl' />
                                </button>

                                <div
                                    ref={thumbScrollRef}
                                    className='flex flex-col gap-3 overflow-y-auto max-h-[540px] scrollbar-thin scrollbar-thumb-white/30 scrollbar-track-transparent pr-2'
                                >
                                    {displayImages.map((img, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedImage(img)}
                                            className={`rounded-xl border-2 transition-all duration-300 overflow-hidden ${selectedImage === img
                                                ? 'border-accent shadow-lg shadow-accent/30'
                                                : 'border-transparent hover:border-white/40'
                                                }`}
                                        >
                                            <img
                                                src={getImageUrl(img)}
                                                alt={`${data.name} ${idx + 1}`}
                                                className='w-20 h-20 object-cover'
                                            />
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => scrollThumbs('down')}
                                    className='mt-2 p-2 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition'
                                >
                                    <FaChevronDown className='text-white text-xl' />
                                </button>
                            </div>

                            {/* Основное изображение */}
                            <div className='flex-1 relative rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-700'>
                                <img
                                    src={getImageUrl(selectedImage || data.thumbnail)}
                                    alt={data.name}
                                    className='w-full h-[540px] object-cover rounded-3xl transform transition-all duration-700 hover:scale-105'
                                />

                                <div className='absolute -top-12 -right-12 w-44 h-44 bg-accent opacity-10 rounded-full blur-3xl animate-float'></div>
                                <div className='absolute -bottom-12 -left-12 w-44 h-44 bg-accent opacity-10 rounded-full blur-3xl animate-float-reverse'></div>

                                <div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-40 -skew-x-12 translate-x-[-100%] hover:translate-x-full transition-transform duration-1000'></div>
                            </div>
                        </div>

                        <div className='flex-1 bg-white/10 backdrop-blur-2xl backdrop-saturate-150 border border-white/20 rounded-3xl p-12 xl:p-20 flex flex-col justify-between shadow-2xl hover:shadow-3xl transition-shadow duration-700 relative overflow-hidden'>
                            <div className='absolute -top-10 -right-10 w-36 h-36 bg-accent opacity-20 rounded-full blur-3xl animate-float'></div>
                            <div className='absolute -bottom-10 -left-10 w-36 h-36 bg-accent opacity-20 rounded-full blur-3xl animate-float-reverse'></div>

                            <h2 className='uppercase text-accent text-lg font-medium mb-2 opacity-90 tracking-widest'>
                                {data.category.category_name}
                            </h2>

                            <h1 className='text-3xl lg:text-4xl font-extrabold mb-6 text-gray-100 leading-snug'>
                                {data.name}
                            </h1>

                            <div className='mb-8 text-gray-200 leading-relaxed'>
                                {formatDescription(showFullDescription ? data.description : `${data.description.slice(0, 300)}...`)}
                                {data.description.length > 300 && (
                                    <button
                                        className="text-accent mt-2 inline-flex items-center gap-2 hover:underline transition-all duration-300"
                                        onClick={toggleDescription}
                                    >
                                        {showFullDescription ? (
                                            <>Скрыть <FaChevronUp /></>
                                        ) : (
                                            <>Читать полностью <FaChevronDown /></>
                                        )}
                                    </button>
                                )}
                            </div>

                            <div className='flex flex-col sm:flex-row sm:items-center sm:gap-x-8 gap-y-4 relative z-10'>
                                <div className='text-3xl text-accent font-bold transform transition-all duration-500 hover:scale-105'>
                                    {(data?.price * kurs).toLocaleString('ru-RU')} UZS
                                </div>
                                <div className='text-2xl text-accent font-semibold transform transition-all duration-500 hover:scale-105'>
                                    ${data.price}
                                </div>
                                <button
                                    onClick={() => addToCart(data, data.id)}
                                    className='btn bg-accent hover:bg-accent/90 text-white rounded-2xl px-6 py-3 transition-all duration-300 shadow-xl hover:shadow-2xl active:scale-95'
                                >
                                    Добавить в корзину
                                </button>
                            </div>

                            <div className='absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-50 blur-md animate-shine'></div>
                        </div>
                    </div>

                    {/* БЛОК "Комплектация" */}
                    <div className='mb-[50px] relative'>
                        <div className='mb-8 text-center'>
                            <h2 className='text-4xl font-bold text-gray-100 mb-2'>Комплектация</h2>
                            <p className='text-xl text-accent/90 font-medium'>
                                {data.brand && <span>{data.brand} </span>}{data.name}
                            </p>
                            <p className='text-sm text-gray-400 mt-2'>Всё, что входит в оригинальную коробку</p>
                        </div>

                        <div className='relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl overflow-hidden'>
                            <div className='absolute inset-0 opacity-10 pointer-events-none'>
                                <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl'></div>
                            </div>

                            <div className='flex overflow-x-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent gap-8 md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-5 md:overflow-x-hidden relative z-10 snap-x snap-mandatory'>
                                {collections.length > 0 ? (
                                    collections.map((item, idx) => (
                                        <div
                                            key={item.id}
                                            className='group flex-none w-64 md:w-auto snap-center flex flex-col items-center text-center'
                                        >
                                            <div className='mb-6 w-48 h-48 md:w-40 md:h-40 rounded-2xl overflow-hidden bg-white/5 shadow-2xl border border-white/10 group-hover:shadow-3xl group-hover:shadow-accent/30 group-hover:border-accent/30 transition-all duration-500 flex items-center justify-center'>
                                                {item.image ? (
                                                    <img
                                                        src={getImageUrl(item.image)}
                                                        alt={item.title}
                                                        className='w-full h-full object-cover'
                                                    />
                                                ) : (
                                                    <FaBox className='text-4xl text-accent/40' />
                                                )}
                                            </div>

                                            <p className='text-gray-200 font-medium text-base leading-tight px-4'>
                                                {item.title}
                                            </p>

                                            {item.description && (
                                                <p className='text-gray-500 text-sm mt-2'>{item.description}</p>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className='col-span-full py-16 text-center'>
                                        <FaBox className='text-white/20 text-6xl mx-auto mb-4' />
                                        <p className='text-gray-400 text-lg'>Комплектация для этого товара не указана</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <RelatedProducts categoryTitle={categoryTitle} />
                </div>
            </div>
        </>
    );
};

export default ProductDetails;